import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { storableError } from '../util/errors';
import { fetchCurrentUser } from './user.duck';
import { hasFavorite, addFavorite, removeFavorite } from '../util/favorites';

export const toggleFavoriteThunk = createAsyncThunk(
  'favorites/toggleFavorite',
  async (listingId, { getState, extra: sdk, dispatch, rejectWithValue }) => {
    const currentUser = getState().user.currentUser;
    const current = currentUser?.attributes?.profile?.privateData?.favorites || [];
    // Delta against the cached server value, preserving existing entry objects.
    // addFavorite is idempotent (guards duplicate ids); adding only writes the
    // minimal { id, savedAt } here — title/image/lastSeen enrichment is Phase 2b.
    const isFav = hasFavorite(current, listingId);
    const updated = isFav ? removeFavorite(current, listingId) : addFavorite(current, listingId);
    try {
      await sdk.currentUser.updateProfile({ privateData: { favorites: updated } });
      // Enforce the refetch: without it, fetchCurrentUser short-circuits when the
      // currentUser entity is < 1s old, returning the stale favorites array and the
      // saved state doesn't stick in-session until a reload (port of 34bde5b83).
      await dispatch(fetchCurrentUser({ enforce: true }));
      return updated;
    } catch (e) {
      return rejectWithValue(storableError(e));
    }
  }
);

const favoritesSlice = createSlice({
  name: 'favorites',
  initialState: { updateInProgress: false, updateError: null, optimisticFavorites: null },
  reducers: {
    setOptimisticFavorites: (state, action) => {
      state.optimisticFavorites = action.payload;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(toggleFavoriteThunk.pending, state => {
        state.updateInProgress = true;
        state.updateError = null;
      })
      .addCase(toggleFavoriteThunk.fulfilled, state => {
        state.updateInProgress = false;
        state.optimisticFavorites = null;
      })
      .addCase(toggleFavoriteThunk.rejected, (state, action) => {
        state.updateInProgress = false;
        state.updateError = action.payload;
        state.optimisticFavorites = null;
      });
  },
});

export const { setOptimisticFavorites } = favoritesSlice.actions;
export default favoritesSlice.reducer;
